import React, { FC } from 'react';
import { Route, useParams } from 'react-router';
import { LoanFormatNavigation } from '../shared/navigation/LoanFormatNavigation';
import { LoanFormat } from '../../models/LoanFormat';
import SubNavigationLayout from '../shared/navigation/SubNavigationLayout';
import { LinkContainer } from '../shared/navigation/NavigationLinksContainer';
import LinkWithIconLeft from '../shared/common/LinkWithIconLeft';
import { LoanFormatIcon } from '../shared/common/GildIcon';
import { BOLINDA_ICON_LIBX, MATERIAL_ICON_CAMPAIGNS, MATERIAL_ICON_LANGUAGE } from '../shared/common/Button/Icon';
import { NavigationLink } from '../shared/navigation/NavigationLink';
import AgeCategorySelector from '../shared/common/AgeCategorySelector';
import { useTranslation } from 'react-i18next';
import LanguageLearningNavigation from '../shared/navigation/LanguageLearningNavigation';
import UnlimitedNavigation from '../shared/navigation/UnlimitedNavigation';
import LibxNavigation from './LibxNavigation';
import { CAMPAIGN_PRODUCTS, useFeature } from '../shared/utils/togglz';
import ShopLanguageDropDown from '../shared/navigation/ShopLanguageDropDown';
import { useAgeCategorySearchParam } from '../shared/utils/useAgeCategorySearchParam';
import { Routes } from 'react-router-dom';
import useShopLanguages from '../shared/hooks/useShopLanguages';
import { useAuthentication } from '../app-store/authentication/useAuthentication';
import LanguageProductsNavigation from '../shared/navigation/LanguageProductsNavigation';

type Props = {
    readonly isSticky: boolean;
};

const ShopLanguageNavigation: FC<Props> = ({ isSticky }) => {
    const { t } = useTranslation();
    const campaignsEnabled = useFeature(CAMPAIGN_PRODUCTS);
    const { shopLanguage } = useParams<'shopLanguage'>();
    const { searchParam: ageCategorySearchParam } = useAgeCategorySearchParam();
    const searchParams = new URLSearchParams(ageCategorySearchParam ? { ageCategory: ageCategorySearchParam } : {});
    const { shopLanguages } = useShopLanguages();
    const isMainShopLanguage = !!shopLanguages.find(sl => sl.shopLanguageId === shopLanguage);
    const {
        spoc: { distributionTerritory: currentTerritory }
    } = useAuthentication();

    const isDETerritory = currentTerritory.startsWith('DE_');

    return (
        <Routes>
            <Route
                path="eaudiobooks/*"
                element={
                    <LoanFormatNavigation
                        isSticky={isSticky}
                        loanFormat={LoanFormat.eAudiobooks}
                        shopLanguage={shopLanguage}
                    />
                }
            />
            <Route
                path="eaudiobooks/languages/:languageId/products"
                element={
                    <LanguageProductsNavigation
                        isSticky={isSticky}
                        shopLanguage={shopLanguage}
                        loanFormat={LoanFormat.eAudiobooks}
                    />
                }
            />
            <Route
                path="ebooks/*"
                element={
                    <LoanFormatNavigation
                        isSticky={isSticky}
                        loanFormat={LoanFormat.eBooks}
                        shopLanguage={shopLanguage}
                    />
                }
            />
            <Route
                path="ebooks/languages/:languageId/products"
                element={
                    <LanguageProductsNavigation
                        isSticky={isSticky}
                        loanFormat={LoanFormat.eBooks}
                        shopLanguage={shopLanguage}
                    />
                }
            />
            <Route
                path="epress/*"
                element={
                    <LoanFormatNavigation
                        ignoreNewReleases={true}
                        isSticky={isSticky}
                        loanFormat={LoanFormat.ePress}
                        shopLanguage={shopLanguage}
                    />
                }
            />
            <Route
                path="epress/languages/:languageId/products"
                element={
                    <LanguageProductsNavigation
                        isSticky={isSticky}
                        loanFormat={LoanFormat.ePress}
                        shopLanguage={shopLanguage}
                    />
                }
            />
            <Route
                path="languagelearning/:languageId/products/:loanFormat/*"
                element={<LanguageLearningNavigation isSticky={isSticky} shopLanguage={shopLanguage} />}
            />
            <Route
                path="languagelearning/:loanFormat/*"
                element={<LanguageLearningNavigation isSticky={isSticky} shopLanguage={shopLanguage} />}
            />
            <Route
                path="unlimited/*"
                element={<UnlimitedNavigation isSticky={isSticky} shopLanguage={shopLanguage} />}
            />
            <Route path="libxchange/*" element={<LibxNavigation isSticky={isSticky} shopLanguage={shopLanguage} />} />
            <Route
                path="*"
                element={
                    <SubNavigationLayout
                        left={shopLanguage ? <ShopLanguageDropDown currentShopLanguage={shopLanguage} /> : null}
                        center={
                            <LinkContainer>
                                <li>
                                    <LinkWithIconLeft to={{ pathname: 'eaudiobooks', search: searchParams.toString() }}>
                                        <LoanFormatIcon aria-hidden={true} icon="eaudiobook-filled" />
                                        <span>{t('menu.shop.eaudiobooksMain')}</span>
                                    </LinkWithIconLeft>
                                </li>
                                <li>
                                    <LinkWithIconLeft to={{ pathname: 'ebooks', search: searchParams.toString() }}>
                                        <LoanFormatIcon aria-hidden={true} icon="ebook-filled" />
                                        <span>{t('menu.shop.ebooksMain')}</span>
                                    </LinkWithIconLeft>
                                </li>
                                <li>
                                    <LinkWithIconLeft to={{ pathname: 'epress', search: searchParams.toString() }}>
                                        <LoanFormatIcon aria-hidden={true} icon="emagazine-filled" />
                                        <span>{t('menu.shop.epressMain')}</span>
                                    </LinkWithIconLeft>
                                </li>
                                <li>
                                    <LinkWithIconLeft
                                        to={{ pathname: 'languagelearning', search: searchParams.toString() }}
                                    >
                                        <i className="material-icons" aria-hidden={true}>
                                            {MATERIAL_ICON_LANGUAGE}
                                        </i>
                                        <span>{t('menu.shop.languages.languageLearning')}</span>
                                    </LinkWithIconLeft>
                                </li>
                                {campaignsEnabled && isMainShopLanguage && !isDETerritory ? (
                                    <li>
                                        <LinkWithIconLeft
                                            to={{ pathname: 'unlimited', search: searchParams.toString() }}
                                        >
                                            <i className="material-icons" aria-hidden={true}>
                                                {MATERIAL_ICON_CAMPAIGNS}
                                            </i>
                                            <span>{t('menu.shop.unlimitedMain')}</span>
                                        </LinkWithIconLeft>
                                    </li>
                                ) : null}
                                <li>
                                    <NavigationLink to={{ pathname: 'libxchange', search: searchParams.toString() }}>
                                        <i className="bolinda-icons" aria-hidden={true}>
                                            {BOLINDA_ICON_LIBX}
                                        </i>
                                        <span>{t('menu.shop.libxchangeMain')}</span>
                                    </NavigationLink>
                                </li>
                            </LinkContainer>
                        }
                        right={!isSticky ? <AgeCategorySelector /> : null}
                        isSticky={isSticky}
                        width="15"
                    />
                }
            />
        </Routes>
    );
};

ShopLanguageNavigation.displayName = 'ShopLanguageNavigation';

export default ShopLanguageNavigation;
